import { Download } from '@carbon/icons-react'
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'
import { LoadingAnimation } from 'components'
import _ from 'lodash'
import { useMemo } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Payload } from 'recharts/types/component/DefaultLegendContent'
import { FiltersT } from '..'
import { formatDate } from '../formatDate'
import { useLegend } from '../GenericLineChart/useLegend'
import { colorScale, parseDate, useDraggableChart } from '../helpers'
import { useOnDownloadImg } from './useOnDownload'

export type SeriesData = {
  id: string
  data: {
    x: string
    y: number
    numOfTests: number
    source: number
    peak: number | undefined
  }[]
}[]

type PropTypes = {
  data: {
    date: string
    value: number
    diseaseName: string
    numOfTests: number
    source: number
    name: string
    peak?: boolean
  }[]
  title?: string
  yAxisTitle: string
  dateRange: FiltersT['dateRange']
  changeDateRange: (start: string, end: string) => void
  onDownload: () => void
  dataStatus: {
    isLoading: boolean
    isError: boolean
    isFetched: boolean
  }
}

export const TestPositivityChart = ({
  data,
  title,
  yAxisTitle,
  dateRange,
  changeDateRange,
  onDownload,
  dataStatus,
}: PropTypes) => {
  const diseaseGrouped = useMemo(() => _.groupBy(data, 'diseaseName'), [data])

  const diseases = useMemo(() => Object.keys(diseaseGrouped), [diseaseGrouped])
  const diseaseSorter = useMemo(
    () =>
      Object.fromEntries(
        diseases.sort((a, b) => a.localeCompare(b)).map((x, i) => [x, i])
      ),
    [diseases]
  )
  const xValues = useMemo(
    () =>
      Array.from(new Set(data.map((x) => x.date))).sort(
        (a, b) => parseDate(a).getTime() - parseDate(b).getTime()
      ),
    [data]
  )

  // Convert to series data format
  const mappedData = useMemo(
    () =>
      Object.entries(diseaseGrouped).map(([diseaseName, row]) => ({
        id: diseaseName,
        data: row.map((d) => {
          return {
            x: d.date,
            y: d.value,
            numOfTests: d.numOfTests,
            source: d.source,
            peak: d.peak ? d.value : undefined,
          }
        }),
      })) as SeriesData,
    [diseaseGrouped]
  )

  // Transform into the format recharts expects
  const transformed = useMemo(
    () =>
      xValues.map((x) => {
        const obj: any = { x }
        mappedData.forEach((diseaseGroup) => {
          const point = diseaseGroup.data.find((d) => d.x === x)
          if (!obj.numOfTests) obj.numOfTests = 0
          obj.numOfTests += point?.numOfTests ?? 0
          obj.source = point?.source
          if (point?.peak) {
            obj[diseaseGroup.id + 'Peak'] = point?.peak
          }
          obj[diseaseGroup.id] = point ? point.y : null
        })
        return obj
      }),
    [mappedData, xValues]
  )

  const numOfTestsExists = useMemo(
    () => transformed.some((x) => x.numOfTests),
    [transformed]
  )

  const {
    refAreaLeft,
    refAreaRight,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useDraggableChart({ changeDateRange })

  const isLoading = dataStatus.isLoading
  const isError = dataStatus.isError
  const isFetched = dataStatus.isFetched
  const noData = transformed.length === 0 && !isLoading && !isError && isFetched

  const {
    handleLegendMouseEnter,
    handleLegendMouseLeave,
    selectLegend,
    selectedLegends,
  } = useLegend()

  const { ref, onDownloadImage } = useOnDownloadImg(
    `${(title ?? 'image').replace(/ /, '_')}_${dateRange.start}_${dateRange.end}.png`
  )

  return (
    <Box
      ref={ref}
      flex={1}
      h='100%'
      borderRadius={'10px'}
      position={'relative'}
      pt={20}
      pb={20}
    >
      <Box
        position='absolute'
        top={4}
        left={4}
        right={4}
        display='flex'
        justifyContent='space-between'
      >
        <Text fontWeight='bold'>{title}</Text>
        <Menu>
          <MenuButton>
            <Button variant={'ghost'} w={'24px'} h={'24px'} p={0}>
              <Download size={16} />
            </Button>
          </MenuButton>
          <MenuList className='ignore-download'>
            <MenuItem onClick={onDownload}>CSV</MenuItem>
            <MenuItem onClick={onDownloadImage}>PNG</MenuItem>
          </MenuList>
        </Menu>
      </Box>
      {isLoading || noData ? (
        <Flex justifyContent={'center'} alignItems={'center'} h='100%' w='100%'>
          {isLoading ? (
            <LoadingAnimation height='100%' />
          ) : isError ? (
            'Something went wrong!'
          ) : (
            'No data available'
          )}
        </Flex>
      ) : (
        <ResponsiveContainer key={yAxisTitle} width='100%' height='100%'>
          <ComposedChart
            data={transformed}
            margin={{ left: 20, right: numOfTestsExists ? 20 : 0 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <XAxis
              type='number'
              dataKey={(d) => parseDate(d.x).getTime()}
              angle={-35}
              textAnchor='end'
              tick={{ fontSize: 12 }}
              tickFormatter={(x) => formatDate(new Date(x))}
              domain={[
                parseDate(dateRange.start).getTime(),
                parseDate(dateRange.end).getTime(),
              ]}
            >
              <Label value='Date' position='bottom' offset={50} />
            </XAxis>
            <YAxis
              yAxisId='left'
              interval='preserveStartEnd'
              scale='linear'
              tick={{ fontSize: 12 }}
            >
              <Label
                angle={270}
                position='left'
                style={{ textAnchor: 'middle' }}
                offset={-10}
              >
                {yAxisTitle}
              </Label>
            </YAxis>
            <YAxis
              yAxisId='right'
              orientation='right'
              interval='preserveStartEnd'
              scale='linear'
              display={numOfTestsExists ? undefined : 'none'}
            >
              {numOfTestsExists && (
                <Label
                  value='Number of Tests'
                  angle={90}
                  offset={-10}
                  style={{ textAnchor: 'middle' }}
                  position='right'
                />
              )}
            </YAxis>
            <CartesianGrid stroke='#eee' strokeDasharray='5 5' />
            {numOfTestsExists && (
              <Bar
                yAxisId='right'
                dataKey='numOfTests'
                fill={'gray'}
                opacity={0.4}
              />
            )}
            {diseases.map((x) => (
              <Line
                key={x}
                yAxisId='left'
                type='monotone'
                dataKey={x}
                stroke={colorScale(x)}
                strokeWidth={selectedLegends.includes(x) ? 3 : 1}
                dot={false}
                connectNulls
              />
            ))}
            {refAreaLeft && refAreaRight && (
              <ReferenceArea
                yAxisId='left'
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
                fill='yellow'
                fillOpacity={0.05}
              />
            )}
            {diseases.map((x) => (
              <Scatter
                yAxisId='left'
                dataKey={x + 'Peak'}
                legendType='none'
                tooltipType='none'
                fill='red'
                shape='circle'
                // key={x}
              />
            ))}

            <Tooltip
              labelFormatter={(x) => formatDate(new Date(x))}
              formatter={(value, name) => [
                name === 'numOfTests'
                  ? parseFloat(value.toString()).toFixed(2)
                  : name === 'Source'
                    ? value
                    : `${parseFloat(value.toString()).toFixed(2)}%`,
                name === 'numOfTests' ? 'Number of Tests' : name,
              ]}
              itemSorter={(item) => diseaseSorter[item.name as string]}
            />
            <Legend
              onClick={selectLegend}
              onMouseOver={handleLegendMouseEnter}
              onMouseOut={handleLegendMouseLeave}
              payload={(
                diseases.map((disease) => ({
                  value: disease,
                  type: 'line',
                  dataKey: disease,
                  color: colorScale(disease),
                })) as Payload[]
              ).concat([
                {
                  value: 'Peak Date',
                  type: 'circle',
                  dataKey: 'Peak Dates',
                  color: 'red',
                },
                ...(numOfTestsExists
                  ? [
                      {
                        value: 'Number of Tests',
                        type: 'rect',
                        dataKey: 'numOfTests',
                        color: 'gray',
                      } as Payload,
                    ]
                  : []),
              ] as Payload[])}
              wrapperStyle={{
                position: 'relative',
                marginTop: 50,
                fontSize: 12,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Box>
  )
}
